// src/services/playersService.js
import { supabase } from '../supabaseClient';

// ─── OUTILS DE RECHERCHE TOLÉRANTE ────────────────────────────────────────────

/** Minuscules, sans accents, sans ponctuation, espaces normalisés */
export const normalizeText = (str) =>
  String(str || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Motif ilike insensible aux accents : les lettres pouvant être accentuées
 * sont remplacées par "_" (n'importe quel caractère). Le filtrage précis
 * est ensuite refait côté application avec normalizeText.
 */
const accentInsensitivePattern = (token) =>
  normalizeText(token).replace(/[aeiouycn]/g, '_');

/** Distance de Levenshtein (nombre de fautes de frappe) */
const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
    prev = cur;
  }
  return prev[b.length];
};

/** "Nadine Aubert" et "Aubert Nadine" donnent la même clé */
export const sortedKey = (nom, prenom) =>
  normalizeText(`${prenom || ''} ${nom || ''}`).split(' ').filter(Boolean).sort().join(' ');

// ─── JOUEURS (table players) ──────────────────────────────────────────────────

/**
 * Rechercher des joueurs par nom/prénom
 * - insensible aux accents et aux majuscules
 * - indépendant de l'ordre ("Aubert Nadine" = "Nadine Aubert")
 */
export const searchPlayers = async (query) => {
  try {
    const tokens = normalizeText(query).split(' ').filter(Boolean);
    if (tokens.length === 0) return { success: true, players: [] };

    let req = supabase.from('players').select('*');
    tokens.forEach((t) => {
      const pat = accentInsensitivePattern(t);
      req = req.or(`nom.ilike.%${pat}%,prenom.ilike.%${pat}%`);
    });
    const { data, error } = await req.order('nom', { ascending: true }).limit(100);
    if (error) throw error;

    const players = (data || [])
      .filter((p) => {
        const full = normalizeText(`${p.prenom || ''} ${p.nom || ''}`);
        return tokens.every((t) => full.includes(t));
      })
      .slice(0, 20);

    return { success: true, players };
  } catch (err) {
    console.error('❌ searchPlayers:', err.message);
    return { success: false, error: err.message, players: [] };
  }
};

/**
 * Chercher des joueurs "ressemblant" à un nom/prénom (détection de doublons).
 * Tolère accents, inversion nom/prénom et petites fautes de frappe.
 */
export const findSimilarPlayers = async (nom, prenom) => {
  try {
    const key = sortedKey(nom, prenom);
    if (!key) return { success: true, players: [] };

    // Candidats : on part des 3 premières lettres de chaque mot saisi
    const words = normalizeText(`${prenom || ''} ${nom || ''}`).split(' ').filter(Boolean);
    const conditions = [];
    words.forEach((w) => {
      const pat = accentInsensitivePattern(w.slice(0, 3));
      conditions.push(`nom.ilike.${pat}%`, `prenom.ilike.${pat}%`);
    });

    const { data, error } = await supabase
      .from('players')
      .select('*')
      .or(conditions.join(','))
      .limit(200);
    if (error) throw error;

    const nomN = normalizeText(nom);
    const prenomN = normalizeText(prenom);

    const similar = (data || [])
      .map((p) => {
        const candKey = sortedKey(p.nom, p.prenom);
        const dist = levenshtein(key, candKey);
        const maxDist = key.length >= 10 ? 2 : 1;
        let ok = dist <= maxDist;

        // Même nom, prénom absent d'un côté ou simple abréviation (ex. "N" / "Nadine")
        if (!ok && nomN && normalizeText(p.nom) === nomN) {
          const pp = normalizeText(p.prenom);
          ok = !prenomN || !pp || pp.startsWith(prenomN) || prenomN.startsWith(pp);
        }
        return ok ? { ...p, _dist: dist } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a._dist - b._dist)
      .slice(0, 5);

    return { success: true, players: similar };
  } catch (err) {
    console.error('❌ findSimilarPlayers:', err.message);
    return { success: false, error: err.message, players: [] };
  }
};

/**
 * Message lisible décrivant un joueur existant (pour les confirmations)
 */
export const describePlayer = (p, classementLabel) =>
  [
    [p.prenom, p.nom].filter(Boolean).join(' '),
    classementLabel || p.dernierClassementCode,
    p.dernierClub,
  ].filter(Boolean).join(' • ');

/**
 * Récupérer tous les joueurs liés à l'utilisateur courant
 */
export const getMyPlayers = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return { success: false, players: [] };

    const { data, error } = await supabase
      .from('user_players')
      .select(`
        id,
        isMyProfile,
        commentaire,
        createdAt,
        players (
          id, nom, prenom, dateNaissance, sexe, paysCode,
          mainDominante, dernierClassementCode, dernierClub,
          dateMajClassement, userId, createdBy, isVerified,
          createdAt, updatedAt
        )
      `)
      .eq('userId', user.id)
      .order('createdAt', { ascending: false });

    if (error) throw error;

    const players = (data || []).map(row => ({
      ...row.players,
      userPlayerId: row.id,
      isMyProfile: row.isMyProfile,
      commentaire: row.commentaire,
    }));

    return { success: true, players };
  } catch (err) {
    console.error('❌ getMyPlayers:', err.message);
    return { success: false, error: err.message, players: [] };
  }
};

/**
 * Créer un nouveau joueur et l'ajouter à la liste de l'utilisateur
 */
export const createPlayer = async (playerData) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Non connecté');

    // 1. Créer le joueur
    const { data: player, error: playerError } = await supabase
      .from('players')
      .insert({
        nom:                  playerData.nom || null,
        prenom:               playerData.prenom || null,
        dateNaissance:        playerData.dateNaissance || null,
        sexe:                 playerData.sexe || null,
        paysCode:             playerData.paysCode || null,
        mainDominante:        playerData.mainDominante || null,
        dernierClassementCode: playerData.dernierClassementCode || null,
        dernierClub:          playerData.dernierClub || null,
        dateMajClassement:    playerData.dernierClassementCode ? new Date().toISOString().slice(0, 10) : null,
        createdBy:            user.id,
        isVerified:           false,
      })
      .select()
      .single();

    if (playerError) throw playerError;

    // 2. Lier à l'utilisateur
    const { error: linkError } = await supabase
      .from('user_players')
      .insert({
        userId:      user.id,
        playerId:    player.id,
        isMyProfile: playerData.isMyProfile || false,
        commentaire: playerData.commentaire || null,
      });

    if (linkError) throw linkError;

    console.log('✅ Joueur créé:', player.id);
    return { success: true, player };
  } catch (err) {
    console.error('❌ createPlayer:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Modifier un joueur (uniquement si créé par l'utilisateur ou admin)
 */
export const updatePlayer = async (playerId, playerData) => {
  try {
    const { data: player, error } = await supabase
      .from('players')
      .update({
        nom:                  playerData.nom || null,
        prenom:               playerData.prenom || null,
        dateNaissance:        playerData.dateNaissance || null,
        sexe:                 playerData.sexe || null,
        paysCode:             playerData.paysCode || null,
        mainDominante:        playerData.mainDominante || null,
        dernierClassementCode: playerData.dernierClassementCode || null,
        dernierClub:          playerData.dernierClub || null,
        updatedAt:            new Date().toISOString(),
      })
      .eq('id', playerId)
      .select()
      .single();

    if (error) throw error;
    return { success: true, player };
  } catch (err) {
    console.error('❌ updatePlayer:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Supprimer un joueur (admin uniquement — géré par RLS)
 */
export const deletePlayer = async (playerId) => {
  try {
    const { error } = await supabase
      .from('players')
      .delete()
      .eq('id', playerId);
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('❌ deletePlayer:', err.message);
    return { success: false, error: err.message };
  }
};

// ─── LIAISONS USER ↔ JOUEUR (table user_players) ─────────────────────────────

/**
 * Ajouter un joueur existant à sa liste
 */
export const linkPlayerToUser = async (playerId, options = {}) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Non connecté');

    const { error } = await supabase
      .from('user_players')
      .upsert({
        userId:      user.id,
        playerId,
        isMyProfile: options.isMyProfile || false,
        commentaire: options.commentaire || null,
      }, { onConflict: 'userId,playerId', ignoreDuplicates: true });

    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('❌ linkPlayerToUser:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Retirer un joueur de sa liste (ne supprime pas le joueur global)
 */
export const unlinkPlayerFromUser = async (userPlayerId) => {
  try {
    const { error } = await supabase
      .from('user_players')
      .delete()
      .eq('id', userPlayerId);
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('❌ unlinkPlayerFromUser:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Mettre à jour le commentaire d'une liaison
 */
export const updateUserPlayer = async (userPlayerId, data) => {
  try {
    const { error } = await supabase
      .from('user_players')
      .update({
        isMyProfile: data.isMyProfile,
        commentaire: data.commentaire,
      })
      .eq('id', userPlayerId);
    if (error) throw error;
    return { success: true };
  } catch (err) {
    console.error('❌ updateUserPlayer:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Vérifier si un joueur est déjà dans la liste de l'utilisateur
 */
export const isPlayerLinked = async (playerId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data } = await supabase
      .from('user_players')
      .select('id')
      .eq('userId', user.id)
      .eq('playerId', playerId)
      .single();

    return !!data;
  } catch {
    return false;
  }
};

// ─── ADMIN : FUSION DE JOUEURS ────────────────────────────────────────────────

/**
 * Tous les joueurs + nombre de matchs rattachés à chacun (admin)
 */
export const getAllPlayersWithCounts = async () => {
  try {
    const { data: players, error } = await supabase
      .from('players')
      .select('*')
      .order('nom', { ascending: true })
      .limit(5000);
    if (error) throw error;

    const counts = {};
    const { data: ms, error: mErr } = await supabase
      .from('matches')
      .select('player1Id, player2Id')
      .limit(20000);
    if (mErr) throw mErr;
    (ms || []).forEach((m) => {
      if (m.player1Id) counts[m.player1Id] = (counts[m.player1Id] || 0) + 1;
      if (m.player2Id) counts[m.player2Id] = (counts[m.player2Id] || 0) + 1;
    });

    return {
      success: true,
      players: (players || []).map((p) => ({ ...p, matchCount: counts[p.id] || 0 })),
    };
  } catch (err) {
    console.error('❌ getAllPlayersWithCounts:', err.message);
    return { success: false, error: err.message, players: [] };
  }
};

/**
 * Fusionne mergeId dans keepId (matchs + liaisons réaffectés, doublon supprimé)
 * Exécuté côté base par la fonction SQL merge_players (admin uniquement).
 */
export const mergePlayers = async (keepId, mergeId) => {
  try {
    const { data, error } = await supabase.rpc('merge_players', {
      p_keep: keepId,
      p_merge: mergeId,
    });
    if (error) throw error;
    return { success: true, result: data };
  } catch (err) {
    console.error('❌ mergePlayers:', err.message);
    return { success: false, error: err.message };
  }
};

// ─── CLASSEMENT ÉVOLUTIF & HISTORIQUE ─────────────────────────────────────────

/**
 * Récupérer un joueur par son id
 */
export const getPlayerById = async (playerId) => {
  try {
    const { data, error } = await supabase
      .from('players')
      .select('*')
      .eq('id', playerId)
      .maybeSingle();
    if (error) throw error;
    return { success: true, player: data };
  } catch (err) {
    console.error('❌ getPlayerById:', err.message);
    return { success: false, error: err.message, player: null };
  }
};

/**
 * Enregistrer un nouveau classement (et/ou club) pour un joueur.
 * Ouvert à tout utilisateur connecté : seule la fonction SQL update_player_ranking
 * est appelée, elle ne touche jamais à l'identité du joueur.
 * - tout est enregistré dans l'historique à sa date
 * - la fiche n'est mise à jour que si la date est >= à la dernière mise à jour connue
 */
export const updatePlayerRanking = async (playerId, { classementCode, club = null, date = null, matchId = null, source = 'manual' }) => {
  try {
    const { data, error } = await supabase.rpc('update_player_ranking', {
      p_player: playerId,
      p_classement: String(classementCode || '').trim(),
      p_club: club,
      p_date: date || new Date().toISOString().slice(0, 10),
      p_match: matchId,
      p_source: source,
    });
    if (error) throw error;
    return { success: true, applied: !!data?.applied };
  } catch (err) {
    console.error('❌ updatePlayerRanking:', err.message);
    return { success: false, error: err.message };
  }
};

/**
 * Historique des classements d'un joueur (du plus récent au plus ancien)
 */
export const getPlayerRankingHistory = async (playerId) => {
  try {
    const { data, error } = await supabase
      .from('player_rankings')
      .select('*')
      .eq('playerId', playerId)
      .order('effectiveDate', { ascending: false })
      .order('createdAt', { ascending: false });
    if (error) throw error;
    return { success: true, history: data || [] };
  } catch (err) {
    console.error('❌ getPlayerRankingHistory:', err.message);
    return { success: false, error: err.message, history: [] };
  }
};